<template>
  <page-main
    :title="`Your accomplishments at ${companyName}`"
    nextText="Done? Go back to the experience overview."
    :buttonTabIndex="nextti"
    eventName="nav"
    @nav="nav('/Work')"
    @save="save()"
  >
    <template v-slot:aside>
      Accomplishments are important - they showcase the impact you made in your
      previous role(s) and what you can bring to your prospective new employer.
      <ul>
        <li>
          Start by thinking about the accomplishments you achieved in this role
          that you are the proudest of or you think are the most important. This
          can include projects, initiatives, actions, results, and training.
        </li>
        <li>
          Accomplishments should be specific to you and a particular experience
          - use examples that focus on your participation as best as you can.
        </li>
        <li>
          Resumation Plus users can use AI to optimize your inputs! Simply
          select the toggle to optimize with AI, and if you want to edit the AI
          phrasing, you can!
        </li>
      </ul>
    </template>
    <template v-slot:nav>
      <v-system-bar color="accent" height="5" class="mt-4"></v-system-bar>
      <modal cardTitle="Example" ctaText="Need an example?">
        <ul class="font-italic">
          <li class="mb-4">
            <span class="font-weight-bold">Examples:</span>
            <ul>
              <li>Launched a website for customer feedback.</li>
              <li>Reduced cost by redesigning machines.</li>
              <li>Developed a marketing plan for Brand A.</li>
              <li>Fixed emergency bug for Product A.</li>
              <li>I learned how to write code.</li>
              <li>Led a team to grow Product A revenue.</li>
            </ul>
          </li>
        </ul>
      </modal>
      <modal cardTitle="Tips" ctaText="Need a few tips?" :accentButton="true">
        <r-tip-container :tips="tips"></r-tip-container>
      </modal>
    </template>
    <v-container>
      <v-row>
        <v-col cols="12" class="r-col">
          <r-expansion-panels v-model="panel">
            <r-expansion-panel v-for="(item, i) in achievements" :key="i">
              <a class="anchor" :id="'panel-' + i"></a>
              <r-expansion-panel-header>
                <div class="d-flex justify-space-between align-center">
                  <div>
                    {{ buildTitleString(item) }}
                  </div>
                  <div class="pr-3 pl-2">
                    <v-btn
                      outlined
                      color="white"
                      @click.stop="
                        launchDialog(item.id, buildTitleString(item))
                      "
                      >Remove</v-btn
                    >
                  </div>
                </div>
              </r-expansion-panel-header>
              <r-expansion-panel-content>
                <v-container>
                  <v-row class="pt-4">
                    <v-col cols="12" class="r-col">
                      <p class="text-body-1 font-italic text--secondary">
                        The questions below are designed to help you present
                        your accomplishment in a way that clear and interesting
                        to decision makers reviewing your resume.
                      </p>
                      <p class="text-body-1 font-italic text--secondary">
                        Be prepared to further expand on these accomplishments
                        during an interview.
                      </p>
                    </v-col>
                    <v-col cols="12" class="r-col">
                      <voice-input
                        v-model="item.background"
                        :multiline="true"
                        :rows="2"
                        label="What is the background of this accomplishment? (One sentence)."
                        hint="Tap the mic to speak, or type your answer"
                      ></voice-input>
                    </v-col>
                    <v-col cols="12" class="r-col">
                      <voice-input
                        v-model="item.responsibility"
                        :multiline="true"
                        :rows="2"
                        label="What was your role or responsibility? (One sentence)."
                        hint="Tap the mic to speak, or type your answer"
                      ></voice-input>
                    </v-col>
                    <v-col cols="12" class="r-col">
                      <voice-input
                        v-model="item.actionResult"
                        :multiline="true"
                        :rows="2"
                        label="What action did you take, or what was the result? (One or two sentences)"
                        hint="Tap the mic to speak, or type your answer"
                      ></voice-input>
                    </v-col>
                    <v-col cols="12" class="r-col">
                      <voice-input
                        v-model="item.measurement"
                        :multiline="true"
                        :rows="2"
                        label="Describe the measured dollar amount, quantity, percent increase / decrease, or other measurement related to the outcome. (If applicable)"
                        hint="Tap the mic to speak, or type your answer"
                      ></voice-input>
                    </v-col>
                    <v-col cols="12" class="pl-5">
                      <h3>Suggested Phrasing:</h3>
                      <p class="text-body-1">{{ item.displayAuto }}</p>
                      <v-switch
                        v-model="item.useCustom"
                        label="Need to optimize with AI or make an adjustment?"
                      ></v-switch>
                      <transition name="line-66">
                        <div>
                          <voice-input
                            v-if="item.useCustom"
                            v-model="item.displayCustom"
                            :multiline="true"
                            :rows="3"
                            label="Adjust the phrase"
                            hint="Tap the mic to refine your phrasing"
                          ></voice-input>
                          <ai-optimize-phrasing
                            v-if="item.useCustom"
                            :phrasing="item.displayCustom"
                            context="work-accomplishment"
                            defaultLength="medium"
                            @onAcceptPhrasing="
                              newPhrasing =>
                                acceptAiSuggestion(item, newPhrasing)
                            "
                          ></ai-optimize-phrasing>
                        </div>
                      </transition>
                    </v-col>
                  </v-row>
                </v-container>
              </r-expansion-panel-content>
            </r-expansion-panel>
          </r-expansion-panels>
        </v-col>
        <v-col cols="12" class="r-col d-flex justify-center">
          <v-btn outlined @click="addAchievement()"
            >Add New Accomplishment</v-btn
          >
        </v-col>
      </v-row>
    </v-container>
    <r-dialog
      v-model="dialog"
      title="You are about to remove this accomplishment:"
    >
      <v-card-text class="text--primary text-body-1 pt-4 pb-3">
        <p class="text-body-1 text--primary">{{ dialogDisplay }}</p>
        <span class="text-body-2">Are you sure?</span>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="gray" text @click="dialog = false"> Cancel </v-btn>
        <v-btn color="error" text @click="removeDialog()"> Remove </v-btn>
      </v-card-actions>
    </r-dialog>
  </page-main>
</template>

<script>
import AiOptimizePhrasing from "../components/AiOptimizePhrasing";
import VoiceInput from "../components/VoiceInput.vue";
import * as uuid from "../utility/guid";
import * as disp from "../utility/display";
import * as log from "../utility/logging/logger";
// PR-WIN-GRADUATE-INTEGRITY: deleting an accomplishment that came from a Small
// Win must un-flag that win, so this page talks to the Small Wins API.
import * as SmallWinsApi from "../api/SmallWinsApi";
import { mapFields } from "vuex-map-fields";
import { mapGetters, mapMutations, mapActions } from "vuex";

// @ is an alias to /src
export default {
  name: "WorkAccomplishments",
  components: {
    "ai-optimize-phrasing": AiOptimizePhrasing,
    "voice-input": VoiceInput
  },
  data: () => ({
    panel: 0,
    dialog: false,
    dialogId: "",
    dialogDisplay: "",
    interval: null,
    // PR-FIX-WORK-CLOBBER: only auto-save when the user has actually edited
    // something on this page. Without this, the 30s timer blindly re-POSTs
    // the whole Work object even when untouched — and SetWork does a
    // wholesale Work replace, so a stale snapshot (this page sitting open in
    // another tab) silently overwrites Work changes made elsewhere, including
    // STARs saved through Sage Coach. Dirty-gating stops the every-30s
    // re-clobber: an idle editor tab never writes.
    dirty: false
  }),
  mounted() {
    let pageKey = this.$route.params.key;
    if (this.key != pageKey) {
      if (!this.doesKeyExist(pageKey)) {
        this.createEntry({ key: pageKey });
      }
      this.key = pageKey;
    }

    if (this.achievements.length == 0) {
      this.addAchievement();
    }

    // set up auto-save 30s interval — gated on an actual dirty edit so an
    // idle/stale editor tab never clobbers Work saved by another path.
    this.interval = setInterval(() => {
      if (this.dirty) {
        this.dirty = false;
        this.saveWork();
      }
    }, 30000);
  },
  beforeDestroy() {
    clearInterval(this.interval);
  },
  watch: {
    achievements: {
      deep: true,
      handler() {
        if (this._updatingDisplay) return;
        // PR-FIX-WORK-CLOBBER: a real user edit to an achievement — mark the
        // page dirty so the next auto-save tick actually persists it. The
        // guarded programmatic display-string pass below never reaches here.
        this.dirty = true;
        this._updatingDisplay = true;
        try {
          this.achievements.forEach(a => {
            if (
              typeof a.background === "undefined" &&
              typeof a.responsibility === "undefined" &&
              typeof a.actionResult === "undefined" &&
              typeof a.measurement === "undefined"
            ) {
              return;
            }

            let bg = a.background || "";
            let resp = a.responsibility || "";
            let ar = a.actionResult || "";
            let meas = a.measurement || "";
            let newAuto = (bg + " " + resp + " " + ar + " " + meas).trim();

            if (
              disp.IsNullorWhitespace(newAuto) &&
              !disp.IsNullorWhitespace(a.displayAuto)
            ) {
              return;
            }

            let setCustomToAuto =
              disp.IsNullorWhitespace(a.displayCustom) ||
              a.displayCustom === a.displayAuto;

            if (a.displayAuto !== newAuto) {
              a.displayAuto = newAuto;
            }

            if (setCustomToAuto && a.displayCustom !== newAuto) {
              a.displayCustom = newAuto;
            }
          });
        } finally {
          this.$nextTick(() => {
            this._updatingDisplay = false;
          });
        }
      }
    }
  },
  methods: {
    acceptAiSuggestion(item, newPhrasing) {
      log.logInformation(`new description [${newPhrasing}]`);
      log.logInformation(`item [${item.displayCustom}]`);
      item.displayCustom = newPhrasing;
      this.save();
    },
    save() {
      this.achievements = this.achievements.filter(
        a =>
          !disp.IsNullorWhitespace(a.displayAuto) ||
          !disp.IsNullorWhitespace(a.background) ||
          !disp.IsNullorWhitespace(a.responsibility) ||
          !disp.IsNullorWhitespace(a.actionResult) ||
          !disp.IsNullorWhitespace(a.measurement)
      );

      this.dirty = false;
      this.saveWork();
    },
    ...mapMutations("work", ["createEntry"]),
    ...mapActions("work", ["saveWork"]),
    nav(routerPath) {
      this.$router.push({ path: routerPath });
    },
    launchDialog(id, text) {
      this.dialogDisplay = text;
      this.dialogId = id;
      this.dialog = true;
    },
    removeDialog() {
      this.removeAchievement(this.dialogId);
      this.dialog = false;
    },
    removeAchievement(id) {
      // PR-WIN-GRADUATE-INTEGRITY: if this accomplishment was graduated from a
      // Small Win, capture the source win id BEFORE removing it so we can
      // un-flag the win (clear its "On resume" badge and re-enable "Add to my
      // resume"). Capture first because the object is gone after the filter.
      let removed = this.achievements.find(item => item.id == id);
      let sourceWinId = removed && removed.sourceWinId;

      this.achievements = this.achievements.filter(item => item.id != id);

      // Persist the Work change, then un-flag the source win. Wrapped so a
      // failure here never blocks the accomplishment delete the user just
      // confirmed.
      this.dirty = false;
      let savePromise = this.saveWork();
      if (sourceWinId) {
        let finish = () => this.ungraduateWin(sourceWinId);
        if (savePromise && typeof savePromise.then === "function") {
          savePromise.then(finish, finish);
        } else {
          finish();
        }
      }
    },
    // PR-WIN-GRADUATE-INTEGRITY: clear the graduated flags on the source Small
    // Win so its "On resume" badge disappears and "Add to my resume" is
    // re-enabled, then reload the wins so the table reflects it. Best-effort:
    // any failure is logged and swallowed.
    ungraduateWin(sourceWinId) {
      return SmallWinsApi.UpdateSmallWin(sourceWinId, {
        graduated: false,
        graduatedAchievementId: null,
        graduatedAt: null
      })
        .then(() => this.$store.dispatch("smallWins/loadSmallWins"))
        .catch(e => {
          log.logError("Failed to un-flag source win after delete", e);
        });
    },
    addAchievement() {
      if (this.achievements.length < 50) {
        this.achievements.push({
          background: "",
          responsibility: "",
          actionResult: "",
          measurement: "",
          displayAuto: "",
          displayCustom: "",
          useCustom: false,
          id: uuid.gen()
        });

        this.panel = this.achievements.length - 1;
        let navObj = {
          path: this.$router.currentRoute.path,
          hash: "panel-" + (this.achievements.length - 1)
        };
        this.$router.push(navObj);
      }
    },
    buildTitleString(item) {
      let len = 0;
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          len = 50;
          break;
        case "sm":
          len = 70;
          break;
        case "md":
          len = 80;
          break;
        case "lg":
          len = 90;
          break;
        case "xl":
          len = 120;
          break;
      }

      return disp.Ellipsis(
        item.useCustom ? item.displayCustom : item.displayAuto,
        len
      );
    }
  },
  computed: {
    ...mapGetters("work", ["doesKeyExist"]),
    ...mapFields("work", {
      key: "key",
      companyName: "workGeneral.companyName",
      achievements: "achievementList"
    }),
    nextti() {
      return "20"; //todo: Fix me
    },
    tips() {
      return [
        "You can put as many as 15 accomplishments per role. This is great for keeping your accomplishment list up to date.",
        "Resumation Plus users can choose whether or not to include accomplishment content by using the checkboxes in each accomplishment header, or by using the ‘Customization’ workflow later on in the process.",
        "You can select and deselect what Accomplishment you want to appear on you resume or use the Customization workflow later in the tool!"
      ];
    }
  }
};
</script>
